// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { IdpMetadataInfo } from '@api/types';
import { StorageKeys } from '@api/constants';
import * as api from '../../../api/setupServices';
import { idpGet } from '@api/commonServices';
import IdpInfoForm, { type FormValues, IdpFormFieldNames } from '../../../components/idpInfoForm/IdpInfoForm';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { getSPMetadataURL } from '@utils/utils';
import { ErrorCode } from '@errorHandler/constants';

const ExternalIDPInfo = ({
  setIdpInfoSaved,
  setMetadataId,
}: {
  setIdpInfoSaved: (idpInfoSaved: boolean) => void;
  setMetadataId: (metadataId: string | null) => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [isLoading, setIsLoading] = useState(false);
  const [idpMetadataInfo, setIdpMetadataInfo] = useState<IdpMetadataInfo>();

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const resp = await idpGet(true);
      const metadata = resp?.idpMetadataInfoList?.[0];
      if (metadata) {
        form.setFieldValue(IdpFormFieldNames.NAME, metadata.idpDisplayName);
        form.setFieldValue(IdpFormFieldNames.URL, metadata.metadataUrl);
        setIdpMetadataInfo(metadata);
      }
      form.setFieldValue(IdpFormFieldNames.DOMAIN, resp?.domainNames?.[0]);
      setIsLoading(false);
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  }, [form.setFieldValue, emitError, clearErrorNotifications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function validateDomain(domain: string) {
    try {
      const domainRequest = { names: [domain] };
      await api.validateDomain(domainRequest);
      return true;
    } catch (error) {
      const e = error as ApiError;
      if (e?.code === ErrorCode.ALREADY_EXISTS) {
        e.code = ErrorCode.DOMAIN_ALREADY_EXISTS;
      }
      emitError(new AppError(e));
      return false;
    }
  }

  const finishForm = async (values: FormValues) => {
    try {
      setIsLoading(true);
      const isValid = await validateDomain(values.IdPDomain);
      if (!isValid) {
        return;
      }
      const response = await api.icpMetadataSave(
        {
          metadataId: idpMetadataInfo?.idpMetadataId,
          displayName: values.IdPName,
          domain: values.IdPDomain,
          metadata:
            values.IdPURL || values.IdPXML
              ? {
                  metadataUrl: values.IdPURL || undefined,
                  rawMetadata: values.IdPXML || undefined,
                }
              : undefined,
        },
        true,
      );
      setMetadataId(response.metadataId);
      sessionStorage.setItem(StorageKeys.EXT_IdP_USED, 'true');
      setIdpInfoSaved(true);
    } catch (error) {
      emitError(new AppError(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IdpInfoForm
      form={form}
      spMetadataURL={getSPMetadataURL()}
      onFinish={finishForm}
      isLoading={isLoading}
      isSetupSkipAllowed={!!sessionStorage.getItem(StorageKeys.IMPR_IDP_USED) || !!idpMetadataInfo}
      setupExistingIdpMetadata={idpMetadataInfo}
    />
  );
};

export default ExternalIDPInfo;
